import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUser } from "@/contexts/UserContext";
import { UserPersona, TeamType, TeamData } from "@/types/user";
import { useToast } from "@/components/ui/use-toast";
import { X, Plus, Calendar, User, Upload } from "lucide-react";
import { connectCalendarProvider } from "@/services/calendarService";
import { meetingsService } from "@/services/meetingsService";
import { useCalendarIntegrations } from "@/hooks/useCalendarIntegrations";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ProfilePictureUpload from "./profile/ProfilePictureUplaud";
import AddGrowthGoalModal from "./modals/AddGrowthGoalModal";
import { fetchUserOrganizationTeams } from "@/components/team/services/teamService";
import {
  fetchGrowthGoals,
  createGrowthGoal,
  deleteGrowthGoal,
} from "@/components/chat/services/goalService";
import { GrowthGoal } from "@/types/user";

const EditProfileForm = () => {
  const { user, updateUserProfile } = useUser();
  const { calendarIntegrations, updateCalendarIntegration, fetchCalendarIntegrations } = useCalendarIntegrations(user?.id);
  const { toast } = useToast();
  const [teams, setTeams] = useState<TeamData[]>([]);

  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [selectedPersona, setSelectedPersona] = useState<UserPersona>(
    user?.persona || "individual"
  );
  const [selectedTeam, setSelectedTeam] = useState<TeamType>(
    user?.teamType || "general"
  );
  const [selectedTeamId, setSelectedTeamId] = useState<string>(
    user?.teamId || "c9eb540e-0671-4151-b472-ee495245bedd"
  );
  const [openToHelp, setOpenToHelp] = useState(user?.openToHelp || false);
  const [skills, setSkills] = useState<string[]>(user?.skills || []);
  const [newSkill, setNewSkill] = useState("");
  const [workHoursStart, setWorkHoursStart] = useState(
    user?.preferredWorkingHours?.start || "09:00"
  );
  const [workHoursEnd, setWorkHoursEnd] = useState(
    user?.preferredWorkingHours?.end || "17:00"
  );
  const [slackHandle, setSlackHandle] = useState(
    user?.contactInfo?.slack || ""
  );
  const [teamsHandle, setTeamsHandle] = useState(
    user?.contactInfo?.teams || ""
  );
  const [phone, setPhone] = useState(user?.contactInfo?.phone || "");
  const [growthGoals, setGrowthGoals] = useState<GrowthGoal[]>([]);
  const [activeTab, setActiveTab] = useState<string>("profile");
  const [connectingCalendar, setConnectingCalendar] = useState<string | null>(
    null
  );
  const [profilePictureDialogOpen, setProfilePictureDialogOpen] =
    useState(false);
  const [isAddGoalModalOpen, setIsAddGoalModalOpen] = useState(false);

  // Update form fields when user data changes
  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setBio(user.bio || "");
      setSelectedPersona(user.persona || "individual");
      setSelectedTeam(user.teamType || "general");
      setSelectedTeamId(user.teamId || "");
      setOpenToHelp(user.openToHelp || false);
      setSkills(user.skills || []);
      setWorkHoursStart(user.preferredWorkingHours?.start || "09:00");
      setWorkHoursEnd(user.preferredWorkingHours?.end || "17:00");
      setSlackHandle(user.contactInfo?.slack || "");
      setTeamsHandle(user.contactInfo?.teams || "");
      setPhone(user.contactInfo?.phone || "");
    }
  }, [user]);

  useEffect(() => {
    const loadTeams = async () => {
      if (!user?.id) return;
      
      const teamsData = await fetchUserOrganizationTeams(user.id);
      if (teamsData) {
        setTeams(teamsData);
       // setSelectedTeamId(user.teamId || teamsData[0]?.id || "");
      }
    };
    loadTeams();
  }, [user?.id]);

  useEffect(() => {
    const loadGrowthGoals = async () => {
      if (user?.id) {
        const goals = await fetchGrowthGoals(user.id);
        setGrowthGoals(goals);
      }
    };

    loadGrowthGoals();
  }, [user?.id]);


  const addSkill = () => {
    if (newSkill.trim() && !skills.includes(newSkill.trim())) {
      setSkills([...skills, newSkill.trim()]);
      setNewSkill("");
    }
  };

  const removeSkill = (skill: string) => {
    setSkills(skills.filter((s) => s !== skill));
  };

  const removeGoal = async (goalId: string) => {
    const success = await deleteGrowthGoal(goalId);

    if (success) {
      setGrowthGoals(growthGoals.filter((g) => g.id !== goalId));
    } else {
      toast({
        title: "Error removing goal",
        description: "Failed to remove your growth goal. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAddGoal = async (goalData: {
    title: string;
    category: string;
    dueDate: Date;
    description: string;
    progress: number;
  }) => {
    if (!user?.id) return;

    const newGoal = await createGrowthGoal(
      user.id,
      {
        userId: user.id,
        title: goalData.title,
        category: goalData.category,
        description: goalData.description,
        dueDate: goalData.dueDate.toISOString(),
        progress: goalData.progress,
      }
    );

    if (newGoal) {
      setGrowthGoals([...growthGoals, newGoal]);
    } else {
      toast({
        title: "Error adding goal",
        description: "Failed to add your growth goal. Please try again.",
        variant: "destructive",
      });
    }
  };


  const handleTeamChange = (teamId: string) => {
    const selectedTeam = teams.find((team) => team.id === teamId);
    if (selectedTeam) {
      setSelectedTeam(selectedTeam.type as TeamType);
      setSelectedTeamId(selectedTeam.id);
      updateUserProfile({
        teamType: selectedTeam.type as TeamType,
        teamId: selectedTeam.id,
        teamName: selectedTeam.name,
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    updateUserProfile({
      name,
      persona: selectedPersona,
      teamType: selectedTeam,
      teamId: selectedTeamId,
      skills,
      bio,
      openToHelp,
      preferredWorkingHours: {
        start: workHoursStart,
        end: workHoursEnd,
      },
      contactInfo: {
        slack: slackHandle,
        teams: teamsHandle,
        phone,
      },
    });

    toast({
      title: "Profile updated",
      description: "Your profile has been updated successfully.",
    });
  };

  const handleConnectCalendar = async (
    provider: "google"
  ) => {
    if (!user?.id) {
      toast({
        title: "Authentication required",
        description: "Please sign in to connect your calendar.",
        variant: "destructive",
      });
      return;
    }

    setConnectingCalendar(provider);
    try {
      const success = await connectCalendarProvider(provider, '', user.id);
      setConnectingCalendar(null);
      
      if (success) {
        toast({
          title: "Calendar connected",
          description: `Your ${provider} calendar has been successfully connected!`,
        });
        // Refresh calendar integrations
        fetchCalendarIntegrations();
      } else {
        toast({
          title: "Error connecting calendar",
          description: `Failed to connect ${provider} calendar. Please try again.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(`Error connecting ${provider} calendar:`, error);
      setConnectingCalendar(null);
      
      toast({
        title: "Error connecting calendar",
        description: `Failed to connect ${provider} calendar. Please try again.`,
        variant: "destructive",
      });
    }
  };

  const handleDisconnectCalendar = async (
    provider: "google"
  ) => {
    if (!user?.id) {
      toast({
        title: "Authentication required",
        description: "Please sign in to disconnect your calendar.",
        variant: "destructive",
      });
      return;
    }

    setConnectingCalendar(provider);
    try {
      // Get calendar integrations to find the integration ID
      const integrationsData = await meetingsService.getCalendarIntegrations();
      const integrations = Array.isArray(integrationsData) ? integrationsData : [];
      
      const integration = integrations.find((int: any) => int.provider === provider);
      
      if (integration) {
        await meetingsService.disconnectCalendar(integration.id);
        await updateCalendarIntegration(provider, false);
        toast({
          title: "Calendar disconnected",
          description: `Your ${provider} calendar has been disconnected.`,
        });
      } else {
        throw new Error(`No ${provider} calendar integration found`);
      }
    } catch (error) {
      console.error(`Error disconnecting ${provider} calendar:`, error);
      toast({
        title: "Error disconnecting calendar",
        description: `Failed to disconnect ${provider} calendar. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setConnectingCalendar(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit Profile</CardTitle>
        <CardDescription>
          Customize your profile and preferences
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
            <TabsTrigger value="integrations">Calendar</TabsTrigger>
          </TabsList>

          <form onSubmit={handleSubmit}>
            <TabsContent value="profile" className="space-y-6">
              <div className="flex flex-col items-center mb-6">
                <Avatar className="w-24 h-24 mb-4">
                  <AvatarImage src={user?.avatarUrl} />
                  <AvatarFallback>
                    <User className="h-12 w-12" />
                  </AvatarFallback>
                </Avatar>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setProfilePictureDialogOpen(true)}
                  className="gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Change Profile Picture
                </Button>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium">Basic Information</h3>

                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">About</Label>
                  <Textarea
                    id="bio"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="A brief bio about yourself"
                    className="min-h-24"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Role</Label>
                  <RadioGroup
                    value={selectedPersona}
                    onValueChange={(value) =>
                      setSelectedPersona(value as UserPersona)
                    }
                    className="space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="individual" id="individual" />
                      <Label htmlFor="individual">Individual Contributor</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="manager" id="manager" />
                      <Label htmlFor="manager">Team Manager</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="newJoiner" id="newJoiner" />
                      <Label htmlFor="newJoiner">New Team Member</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="admin" id="admin" />
                      <Label htmlFor="admin">Administrator</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label>Team</Label>
                  <Select
                    value={selectedTeamId}
                    onValueChange={handleTeamChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your team" />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium">Skills & Expertise</h3>

                <div className="space-y-2">
                  <Label>Skills</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {skills.map((skill) => (
                      <div
                        key={skill}
                        className="bg-muted px-3 py-1 rounded-full flex items-center gap-1"
                      >
                        <span className="text-sm">{skill}</span>
                        <button
                          type="button"
                          className="h-5 w-5 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
                          onClick={() => removeSkill(skill)}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a skill"
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && (e.preventDefault(), addSkill())
                      }
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addSkill}
                      className="shrink-0"
                    >
                      Add
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="openToHelp">Open to helping others</Label>
                    <Switch
                      id="openToHelp"
                      checked={openToHelp}
                      onCheckedChange={setOpenToHelp}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Other team members can reach out to you for help with skills
                    you're proficient in
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium">Growth Goals</h3>

                <div className="space-y-2">
                  <Label>Personal development goals</Label>
                  <div className="space-y-2 mb-2">
                    {growthGoals.map((goal) => (
                      <div
                        key={goal.id}
                        className="flex items-center justify-between border p-2 rounded-md"
                      >
                        <div>
                          <div className="font-medium">{goal.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {goal.category} - {goal.description}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Due:{" "}
                            {goal.dueDate
                              ? new Date(goal.dueDate).toLocaleDateString()
                              : "No due date"}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Progress: {goal.progress}%
                          </div>
                        </div>
                        <button
                          type="button"
                          className="h-6 w-6 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
                          onClick={() => removeGoal(goal.id)}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsAddGoalModalOpen(true)}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Goal
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="preferences" className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-medium">Preferences</h3>

                <div className="space-y-2">
                  <Label>Working Hours</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={workHoursStart}
                      onChange={(e) => setWorkHoursStart(e.target.value)}
                      className="w-32"
                    />
                    <span>to</span>
                    <Input
                      type="time"
                      value={workHoursEnd}
                      onChange={(e) => setWorkHoursEnd(e.target.value)}
                      className="w-32"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Contact Information</Label>
                  <div className="space-y-2">
                    <div className="flex gap-2 items-center">
                      <div className="w-20 text-sm text-muted-foreground">
                        Slack
                      </div>
                      <Input
                        value={slackHandle}
                        onChange={(e) => setSlackHandle(e.target.value)}
                        placeholder="@username"
                      />
                    </div>
                    <div className="flex gap-2 items-center">
                      <div className="w-20 text-sm text-muted-foreground">
                        Teams
                      </div>
                      <Input
                        value={teamsHandle}
                        onChange={(e) => setTeamsHandle(e.target.value)}
                        placeholder="username@company.com"
                      />
                    </div>
                    <div className="flex gap-2 items-center">
                      <div className="w-20 text-sm text-muted-foreground">
                        Phone
                      </div>
                      <Input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="integrations" className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-medium">Calendar Integrations</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Connect your calendars to see your schedule and availability
                  in Step
                </p>

                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 text-blue-600 p-2 rounded-full">
                          <Calendar size={20} />
                        </div>
                        <div>
                          <h4 className="font-medium">Google Calendar</h4>
                          <p className="text-sm text-muted-foreground">
                            {calendarIntegrations?.google
                              ? "Connected"
                              : "Sync your Google Calendar events"}
                          </p>
                        </div>
                      </div>
                      {connectingCalendar === "google" ? (
                        <Button disabled>
                          <span className="animate-spin mr-2">⟳</span>
                          Connecting...
                        </Button>
                      ) : calendarIntegrations?.google ? (
                        <Button
                          variant="outline"
                          onClick={() => handleDisconnectCalendar("google")}
                        >
                          Disconnect
                        </Button>
                      ) : (
                        <Button onClick={() => handleConnectCalendar("google")}>
                          Connect
                        </Button>
                      )}
                    </div>
                  </div>

                </div>
              </div>

            </TabsContent>

            <div className="flex justify-end mt-6">
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </Tabs>
      </CardContent>

      <ProfilePictureUpload
        isOpen={profilePictureDialogOpen}
        onOpenChange={setProfilePictureDialogOpen}
      />

      <AddGrowthGoalModal
        isOpen={isAddGoalModalOpen}
        onOpenChange={setIsAddGoalModalOpen}
        onSubmit={handleAddGoal}
      />
    </Card>
  );
};

export default EditProfileForm;
